// client/src/components/pages/LexiStar/Commissioning/Commissioning.tsx (converted from .jsx, ST-00193)
//
// LexiStar commissioning — parallel run and certificate (ST-00188, M3-U1;
// T4-S15; LS-09 §5.20). Routes /lexistar/commissioning (runs + a new run) and
// /lexistar/commissioning/:id (the five-step workspace, CommissioningRun). The
// printable certificate is Certificate.jsx at /lexistar/commissioning/:id/certificate.
// Page permission lexistar.commissioning.manage.
//
// A guided workspace for the first weeks at a customer: LexiStar's trips,
// distances, litres and ticket matches measured against the customer's own
// records, per truck and lane against agreed floors, ending in a certificate
// the customer keeps either way.
//
// ST-00193: a floor override takes two users in two sessions. The list shows the
// runs whose override request awaits a second user, with the way in for a
// different authorised user (the requester sees that someone else must sign it).

import { useCallback, useEffect, useMemo, useState, type ComponentType, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { FaCertificate, FaPlus, FaUserCheck } from 'react-icons/fa';
import ThemedSelect from '../../../common/ThemedSelect';
import UnifiedDateTimeRangePicker from '../../../common/UnifiedDateTimeRangePicker';
import { useAuth } from '../../../../context/AuthContext';
import {
  listParallelRuns as listParallelRunsJs,
  createParallelRun as createParallelRunJs,
  pickList as pickListJs,
  pickObject as pickObjectJs,
} from '../../../../services/lexistarAssurance';
import { listLanes as listLanesJs } from '../../../../services/lexistarStatusBoard';
import { listAssets as listAssetsJs } from '../../../../services/lexistarAssets';
import {
  PageHeader,
  ErrorBanner,
  LoadingState,
  EmptyState,
  NoPermissionPage,
  cardClasses,
  inputClasses,
} from '../StatusBoard/atoms';
import { fmtDateTime as fmtDateTimeJs, fmtNum as fmtNumJs } from '../StatusBoard/format';
import {
  Field as FieldJs,
  InlineError as InlineErrorJs,
  buttonClasses,
} from '../ReviewQueue/assuranceAtoms';
import {
  errorMessage as errorMessageJs,
  localToIso as localToIsoJs,
  toLocalValue as toLocalValueJs,
} from '../ReviewQueue/assuranceShared';
import {
  DEFAULT_FLOORS as DEFAULT_FLOORS_JS,
  METRICS as METRICS_JS,
  RUN_STATE_META as RUN_STATE_META_JS,
  floorText as floorTextJs,
} from './commissioningLogic';
import CommissioningRunJs from './CommissioningRun';
import { runsAwaitingSecondUser, type PendingOverride } from './overrideRequest';

// ── Shapes (GET /lexistar/parallel-runs; m3-contracts §7.1) ─────────────────

/** A parallel run as the list returns it. */
interface ParallelRunRow {
  id: string;
  name: string;
  state: string;
  window_from: string;
  window_to: string;
  asset_keys?: string[] | null;
  floors?: Record<string, number> | null;
  certificate_id?: string | null;
  pending_override?: PendingOverride | null;
}

/** A run with a live override request, as the pending list shows it. */
type AwaitingRun = ParallelRunRow & { pending_override: PendingOverride; status: 'mine' | 'theirs' };

interface MetricMeta {
  key: string;
  label: string;
  kind: 'rate' | 'pct';
}

interface Choice {
  value: string;
  label: string;
}

interface LaneRow {
  id: string | number;
  name?: string | null;
  from_name?: string | null;
  from_location_name?: string | null;
  to_name?: string | null;
  to_location_name?: string | null;
}

interface AssetRow {
  asset_type?: string | null;
  registration?: string | null;
  registration_normalised?: string | null;
  fleet_no?: string | null;
}

const listParallelRuns = listParallelRunsJs as unknown as (params?: object) => Promise<unknown>;
const createParallelRun = createParallelRunJs as unknown as (data: object) => Promise<unknown>;
const pickList = pickListJs as unknown as <T>(data: unknown, ...keys: string[]) => T[];
const pickObject = pickObjectJs as unknown as <T>(data: unknown, ...keys: string[]) => T | null;
const listLanes = listLanesJs as unknown as (params?: object) => Promise<unknown>;
const listAssets = listAssetsJs as unknown as (params?: object) => Promise<{ assets?: AssetRow[] } | null>;
const fmtDateTime = fmtDateTimeJs as unknown as (iso: string | null | undefined) => string;
const fmtNum = fmtNumJs as unknown as (n: number) => string;
const errorMessage = errorMessageJs as unknown as (err: unknown, fallback?: string) => string;
const localToIso = localToIsoJs as unknown as (s: string) => string | null;
const toLocalValue = toLocalValueJs as unknown as (d: Date) => string;
const DEFAULT_FLOORS = DEFAULT_FLOORS_JS as Record<string, number>;
const METRICS = METRICS_JS as unknown as MetricMeta[];
const RUN_STATE_META = RUN_STATE_META_JS as Record<string, { label: string; glyph: string }>;
const floorText = floorTextJs as unknown as (meta: MetricMeta, floor: number | undefined) => string;
const Field = FieldJs as unknown as ComponentType<{
  label: ReactNode;
  htmlFor?: string;
  hint?: ReactNode;
  children: ReactNode;
}>;
const InlineError = InlineErrorJs as unknown as ComponentType<{ message?: ReactNode }>;
const CommissioningRun = CommissioningRunJs as unknown as ComponentType<{
  id: string;
  lanesById?: Record<string, string>;
}>;

const DAY = 86400000;

function NewRunForm({
  trucks,
  lanes,
  onCancel,
  onCreated,
}: {
  trucks: Choice[];
  lanes: Choice[];
  onCancel: () => void;
  onCreated: (run: ParallelRunRow | null) => void;
}) {
  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);
  const [name, setName] = useState('');
  const [start, setStart] = useState(toLocalValue(today));
  const [end, setEnd] = useState(toLocalValue(new Date(today.getTime() + 14 * DAY)));
  const [assetKeys, setAssetKeys] = useState<string[]>([]);
  const [laneIds, setLaneIds] = useState<string[]>([]);
  const [floors, setFloors] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      Object.entries(DEFAULT_FLOORS).map(([k, v]) => [k, String(+(v * 100).toFixed(1))])
    )
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    const from = localToIso(start);
    const to = localToIso(end);
    if (!name.trim()) return setError('Name the run, e.g. “Mine A — October parallel run”.');
    if (!from || !to || new Date(to) <= new Date(from))
      return setError('Choose a window that ends after it starts.');
    if (!assetKeys.length) return setError('Choose the trucks in the run.');
    const f: Record<string, number> = {};
    for (const m of METRICS) {
      const v = Number(floors[m.key]);
      if (!Number.isFinite(v) || v < 0 || v > 100)
        return setError(`The ${m.label.toLowerCase()} floor must be a percentage.`);
      f[m.key] = Math.round(v * 10) / 1000;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await createParallelRun({
        name: name.trim(),
        window_from: from,
        window_to: to,
        asset_keys: assetKeys,
        lane_ids: laneIds,
        floors: f,
      });
      onCreated(pickObject<ParallelRunRow>(res, 'parallel_run', 'run'));
    } catch (err) {
      setError(errorMessage(err, 'The run was not created.'));
    } finally {
      setBusy(false);
    }
    return undefined;
  };

  const asList = (v: unknown): string[] => (Array.isArray(v) ? v.map(String) : []);

  return (
    <section className={`${cardClasses} p-4 sm:p-5 space-y-4`} aria-label="New parallel run">
      <div>
        <h2 className="text-base font-bold text-slate-900">Step 1 · Scope</h2>
        <p className="text-sm text-slate-600">
          The window, the trucks and the lanes to measure, and the floors they must meet.
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Name" htmlFor="run-name">
          <input
            id="run-name"
            className={inputClasses}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Mine A — October parallel run"
          />
        </Field>
        <Field label="Window" hint="Usually two to four weeks.">
          <UnifiedDateTimeRangePicker
            startValue={start}
            endValue={end}
            onStartChange={setStart}
            onEndChange={setEnd}
          />
        </Field>
        <Field label="Trucks">
          <ThemedSelect
            isMulti
            value={assetKeys}
            onChange={(e) => setAssetKeys(asList(e.target.value))}
            aria-label="Trucks"
            placeholder="Choose trucks…"
          >
            {trucks.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </ThemedSelect>
        </Field>
        <Field label="Lanes" hint="Leave empty for every lane the trucks run.">
          <ThemedSelect
            isMulti
            value={laneIds}
            onChange={(e) => setLaneIds(asList(e.target.value))}
            aria-label="Lanes"
            placeholder="All lanes"
          >
            {lanes.map((l) => (
              <option key={l.value} value={l.value}>
                {l.label}
              </option>
            ))}
          </ThemedSelect>
        </Field>
      </div>
      <fieldset>
        <legend className="block text-sm font-medium text-slate-700 mb-1.5">Floors (%)</legend>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {METRICS.map((m) => (
            <label key={m.key} className="block text-xs text-slate-600">
              {m.label} <span className="text-slate-400">{m.kind === 'rate' ? '≥' : '≤'}</span>
              <input
                type="number"
                min="0"
                max="100"
                step="0.5"
                value={floors[m.key]}
                onChange={(e) => setFloors((f) => ({ ...f, [m.key]: e.target.value }))}
                className={`${inputClasses} mt-1 tabular-nums`}
              />
            </label>
          ))}
        </div>
        <p className="mt-1 text-xs text-slate-500">
          Defaults from LS-02: trips detected ≥ 98%, distance within 2%, measured fuel within 3%,
          estimated fuel within 8%, tickets matched ≥ 95%.
        </p>
      </fieldset>
      <InlineError message={error} />
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onCancel} className={buttonClasses.secondary}>
          Cancel
        </button>
        <button type="button" onClick={submit} disabled={busy} className={buttonClasses.primary}>
          {busy ? 'Creating…' : 'Create the run'}
        </button>
      </div>
    </section>
  );
}

/** Runs whose floor override waits for a second user (SR-21, ST-00193). */
function AwaitingSecondUser({ runs }: { runs: AwaitingRun[] }) {
  if (!runs.length) return null;
  return (
    <section className={`${cardClasses} p-4 sm:p-5 space-y-3`} aria-label="Awaiting a second user">
      <div>
        <h2 className="flex items-center gap-2 text-base font-bold text-slate-900">
          <FaUserCheck size={13} aria-hidden="true" /> Overrides awaiting a second user
        </h2>
        <p className="text-sm text-slate-600">
          Each was asked for below a floor and needs a different authorised user to sign it off,
          from their own login.
        </p>
      </div>
      <ul className="divide-y divide-slate-200">
        {runs.map((r) => (
          <li
            key={r.id}
            className="py-2.5 flex flex-wrap items-center justify-between gap-x-4 gap-y-1.5"
          >
            <div className="min-w-0">
              <p className="text-sm font-semibold text-slate-900">{r.name}</p>
              <p className="text-xs text-slate-600">
                Asked by {r.status === 'mine' ? 'you' : r.pending_override.requested_by} ·{' '}
                {fmtDateTime(r.pending_override.requested_at)} · expires{' '}
                {fmtDateTime(r.pending_override.expires_at)}
              </p>
            </div>
            {r.status === 'theirs' ? (
              <Link
                to={`/lexistar/commissioning/${r.id}?step=signoff`}
                className={buttonClasses.primary}
              >
                Review and sign off
              </Link>
            ) : (
              <span className="text-xs font-medium text-slate-600">
                Waiting for a different authorised user
              </span>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

const Commissioning = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasPermission, user } = useAuth();
  const canManage = hasPermission('lexistar.commissioning.manage');
  const [runs, setRuns] = useState<ParallelRunRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [trucks, setTrucks] = useState<Choice[]>([]);
  const [lanes, setLanes] = useState<Choice[]>([]);

  const load = useCallback(async () => {
    if (!canManage || id) return;
    try {
      setLoading(true);
      const res = await listParallelRuns();
      setRuns(pickList<ParallelRunRow>(res, 'parallel_runs', 'runs'));
      setError(null);
    } catch (err) {
      setError(errorMessage(err, 'Failed to load parallel runs.'));
    } finally {
      setLoading(false);
    }
  }, [canManage, id]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!canManage) return;
    listLanes()
      .then((res) =>
        setLanes(
          pickList<LaneRow>(res, 'lanes').map((l) => ({
            value: String(l.id),
            label:
              l.name ||
              `${l.from_name || l.from_location_name} → ${l.to_name || l.to_location_name}`,
          }))
        )
      )
      .catch(() => setLanes([]));
    if (!creating) return;
    listAssets({ limit: 500 })
      .then((res) =>
        setTrucks(
          (res?.assets || [])
            .filter((a) => a.asset_type !== 'trailer' && a.registration_normalised)
            .map((a) => ({
              value: `reg:${a.registration_normalised}`,
              label: a.fleet_no ? `${a.registration} (${a.fleet_no})` : String(a.registration),
            }))
        )
      )
      .catch(() => setTrucks([]));
  }, [canManage, creating]);

  const lanesById = useMemo(
    () => Object.fromEntries(lanes.map((l) => [l.value, l.label])),
    [lanes]
  );
  const awaiting = useMemo(() => runsAwaitingSecondUser(runs, user), [runs, user]);

  const header = (
    <PageHeader
      icon={FaCertificate}
      title="Commissioning"
      subtitle="Parallel run against the customer's own records, ending in a Trip Reconciliation Certificate."
      actions={
        canManage && !id && !creating ? (
          <button type="button" onClick={() => setCreating(true)} className={buttonClasses.primary}>
            <FaPlus size={11} /> <span className="hidden sm:inline">New parallel run</span>
          </button>
        ) : null
      }
    />
  );

  if (!canManage) {
    return (
      <NoPermissionPage
        header={header}
        message="You do not have permission to manage commissioning."
      />
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {header}
      <div className="max-w-[1500px] mx-auto px-3 sm:px-6 py-4 sm:py-6 space-y-4">
        {id ? (
          <CommissioningRun id={id} lanesById={lanesById} />
        ) : (
          <>
            <ErrorBanner message={error} onDismiss={() => setError(null)} />
            {creating && (
              <NewRunForm
                trucks={trucks}
                lanes={lanes}
                onCancel={() => setCreating(false)}
                onCreated={(run) => {
                  setCreating(false);
                  if (run?.id) navigate(`/lexistar/commissioning/${run.id}?step=records`);
                  else load();
                }}
              />
            )}
            <AwaitingSecondUser runs={awaiting} />
            {loading && runs.length === 0 ? (
              <LoadingState label="Loading parallel runs..." />
            ) : runs.length === 0 ? (
              !creating && (
                <div className={cardClasses}>
                  <EmptyState icon={FaCertificate} title="No parallel runs yet">
                    Start one in the first weeks at a customer: load their trip sheets, tickets and
                    fuel issues, and LexiStar is measured against them, truck by truck.
                  </EmptyState>
                </div>
              )
            ) : (
              <ul className="grid grid-cols-1 lg:grid-cols-2 gap-3" aria-label="Parallel runs">
                {runs.map((r) => (
                  <li key={r.id} className={`${cardClasses} p-4`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <Link
                          to={`/lexistar/commissioning/${r.id}`}
                          className="text-base font-bold text-slate-900 hover:underline"
                        >
                          {r.name}
                        </Link>
                        <p className="text-xs text-slate-600 tabular-nums">
                          {fmtDateTime(r.window_from)} → {fmtDateTime(r.window_to)} ·{' '}
                          {fmtNum((r.asset_keys || []).length)} trucks
                        </p>
                      </div>
                      <span className="text-xs font-semibold text-slate-700 whitespace-nowrap">
                        {RUN_STATE_META[r.state]?.glyph} {RUN_STATE_META[r.state]?.label || r.state}
                      </span>
                    </div>
                    <p className="mt-2 text-xs text-slate-500">
                      Floors:{' '}
                      {METRICS.map(
                        (m) => `${m.label.toLowerCase()} ${floorText(m, r.floors?.[m.key])}`
                      ).join(' · ')}
                    </p>
                    {r.certificate_id && (
                      <Link
                        to={`/lexistar/commissioning/${r.id}/certificate`}
                        className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium text-primary-700 hover:underline"
                      >
                        <FaCertificate size={11} /> Certificate
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Commissioning;
